import { booleanAttribute, Component, HostBinding, input, Input, OnInit, output, ViewChild } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatTooltipModule, TooltipPosition } from '@angular/material/tooltip';

import { selfOrParentHasClass } from '../../helpers';
import { FormControlErrors } from '../form-control-errors/form-control-errors';
import { Icon } from '../icon';
import { Tooltip } from '../tooltip';

import { BooleanFieldValue } from 'app/decorators';

@Component({
    selector: 'stby-checkbox',
    styleUrl: './checkbox.scss',
    templateUrl: './checkbox.html',
    imports: [FormControlErrors, MatTooltipModule, Tooltip, Icon],
})
export class Checkbox implements OnInit {
    @ViewChild('contentWrapper') contentWrapper;

    readonly alwaysShowErrors = input(false, { transform: booleanAttribute });
    readonly noSpacing = input(false, { transform: booleanAttribute });
    readonly spacingSmall = input(false, { transform: booleanAttribute });
    readonly errored = input(false, { transform: booleanAttribute });
    readonly onlyAllowCheckboxClick = input(false, { transform: booleanAttribute });

    @Input() @HostBinding('class') checkboxStyle = 'default';
    @Input() label = '';
    readonly requiredLabel = input<string>();

    readonly labelHasTabindex = input(false);
    readonly id = input<string>('');

    // Caution! Don't use controlName in modals because in modals the form group can be initialized later than formControl
    // and so isFormControl() here will return false, causing the formControl to not be updated
    readonly controlName = input<string>();
    readonly group = input<AbstractControl>();

    @Input() tooltip: string;
    readonly tooltipPosition = input<TooltipPosition>('above');

    _disabled = false;

    @Input()
    @BooleanFieldValue()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: boolean) {
        this._disabled = value;
        this.setFormControlDisabledStatus(value);
    }

    _checked: boolean;

    @Input()
    @BooleanFieldValue()
    get checked(): boolean {
        return this._checked;
    }
    set checked(value: boolean) {
        this.setChecked(value);
    }

    _indeterminate: boolean;
    @Input()
    get indeterminate(): boolean {
        return this._indeterminate;
    }
    set indeterminate(value: boolean) {
        this.setIndeterminate(value);
    }

    readonly checkedChangeCallback = input<() => void>();
    readonly checkedChange = output<boolean>();

    formControl: AbstractControl;

    constructor() {}

    ngOnInit() {
        this.populateFormControl();
        this.initFormControl();
    }

    private initFormControl() {
        const formControl = this.group()?.get(this.controlName());

        if (formControl) {
            this._disabled = formControl.disabled;

            formControl.statusChanges.subscribe(() => {
                this._disabled = formControl.disabled;
            });
        }
    }

    handleChange() {
        this.checkedChange.emit(this.checked);
    }

    setChecked(value: boolean): void {
        if (value !== this._checked) {
            this._checked = value;
            this.setFormControlValue(value);
        }
    }

    setIndeterminate(value: boolean): void {
        if (value !== this._indeterminate) {
            this._indeterminate = value;
        }
    }

    toggleChecked(event: Event) {
        if (this.onlyAllowCheckboxClick() && !selfOrParentHasClass(event.target as Element, 'checkbox', true)) {
            return;
        }
        if (this.disabled || selfOrParentHasClass(event.target as Element, 'js-deny-value-setting', true)) {
            return;
        }

        // Event emits get cancelled when stopping propagation so the preferred method is a callback function
        const checkedChangeCallback = this.checkedChangeCallback();
        if (typeof checkedChangeCallback === 'function') {
            checkedChangeCallback();
            event.stopPropagation();
        } else {
            this.checked = !this.checked;
            this.handleChange();
        }
    }

    private isFormControl(): boolean {
        this.populateFormControl();
        return !!this.formControl;
    }

    private populateFormControl(): void {
        const group = this.group();
        const controlName = this.controlName();
        if (!group || !controlName) {
            this.formControl = null;
            return;
        }
        this.formControl = group.get(controlName);
    }

    private setFormControlDisabledStatus(status: boolean): void {
        if (this.isFormControl()) {
            if (status) {
                this.formControl.disable();
            } else {
                this.formControl.enable();
            }
        }
    }

    private setFormControlValue(value: any): void {
        if (this.isFormControl()) {
            this.formControl.setValue(value);
        }
    }
}
